import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { analyticsService, userBannerManager, userStatusManager } from "@core/service-container";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import { useUser } from "@domain/user/hooks/use-user";
import { RegistrationStatus } from "@domain/user/user";
import styled from "@emotion/styled";
import { AlertCard, CardType } from "@ui/components/common/alert-card";
import { RoutesPath } from "@ui/navigation/routes";
import { computeDiffBetween, now } from "@utils/date-utils";
import { isNil } from "lodash";
import { useObservable } from "micro-observables";
import React, { useEffect, useState } from "react";

export const ValidateAccountCard: React.FC<{
	hideForValidatedPt?: boolean;
	showForPendingPt: boolean;
	className?: string;
}> = ({ hideForValidatedPt, showForPendingPt, className }) => {
	const { format } = useI18n();
	const navigate = useNavigate();
	const ptRegistrationStatus = useObservable(userStatusManager.ptRegistrationStatus);
	const [hideBanner, setHideBanner] = useState(false);
	const user = useUser();
	const currentPtId = user?.id ? user.id : "";

	const closeValidatedBanner = () => {
		setHideBanner(true);
		userBannerManager.updateValidatedAccountBanner(currentPtId);
	};
	useEffect(() => {
		if (userBannerManager.hasValidatedAccountBanner(currentPtId)) {
			setHideBanner(true);
		} else if (!isNil(user?.hcpValidationDate) && !isNil(user.identityValidationDate) && !hideBanner) {
			const currentDate = now();
			const { month: hcpMonthElapsed } = computeDiffBetween(user.hcpValidationDate, currentDate);
			const { month: validationMonthElapsed } = computeDiffBetween(user.identityValidationDate, currentDate);

			if (hcpMonthElapsed > 0 && validationMonthElapsed > 0) {
				setHideBanner(true);
			}
		}
	}, [ptRegistrationStatus, hideBanner, user?.hcpValidationDate, user?.id, user?.identityValidationDate, currentPtId]);

	switch (ptRegistrationStatus) {
		case RegistrationStatus.PENDING:
			return (
				showForPendingPt && (
					<Card
						className={className}
						cardType={CardType.Pending}
						title={format("home.validatePendingAccountCard.title")}
						message={format("home.validatePendingAccountCard.message")}
					/>
				)
			);
		case RegistrationStatus.INCOMPLETE:
			return (
				<Card
					className={className}
					cardType={CardType.Info}
					title={format("home.validateAccountCard.title")}
					message={format("home.validateAccountCard.message")}
					secondaryButtons={[
						{
							title: format("home.validateAccountCard.cta"),
							onPressed: () => {
								analyticsService.trackEvent({
									category: AnalyticsCategory.REGISTRATION,
									action: "startVerificationFlowCTA",
									params: ["patients"],
								});

								navigate(RoutesPath.ProfessionalInformation);
							},
						},
					]}
				/>
			);
		case RegistrationStatus.VALIDATED:
			return (
				hideForValidatedPt &&
				!hideBanner && (
					<Card
						className={className}
						cardType={CardType.Validated}
						title={format("settings.AccountValidatedAccountCard.title")}
						message={format("settings.AccountValidatedAccountCard.message")}
						onCrossClicked={closeValidatedBanner}
					/>
				)
			);
		default:
			return null;
	}
};

const Card = styled(AlertCard)`
	margin-top: 24px;
`;
